import type { Location, NodeEntry } from 'slate';
import { Editor } from 'slate';

import { calculateDimensionsWithRotation, emuToPx, stAngleToDegrees } from '../../util';
import { isDrawingElement, Queries } from '../internal';
import type { cNvPr, DrawingElement } from '../types';

interface DrawingDimensions {
  containerHeightPx: number;
  containerWidthPx: number;
  imgHeightPx: number;
  imgWidthPx: number;
}

interface VisualProperties {
  flippedHorizontal: boolean;
  flippedVertical: boolean;
  rotation: number;
}

export const drawingQueries = {
  getDrawingDimensions: (drawing: DrawingElement): DrawingDimensions => {
    const extent = drawing?.inline?.graphic?.graphicData?.pic?.spPr?.xfrm?.ext;
    const imgWidthPx = emuToPx(parseInt(extent?.cx ?? '0'));
    const imgHeightPx = emuToPx(parseInt(extent?.cy ?? '0'));
    const { rotation } = Queries.getDrawingVisualProperties(drawing);
    const { containerHeight, containerWidth } = calculateDimensionsWithRotation({
      height: imgHeightPx,
      rotation,
      width: imgWidthPx,
    });

    return {
      containerHeightPx: containerHeight,
      containerWidthPx: containerWidth,
      imgHeightPx,
      imgWidthPx,
    };
  },
  /**
   * Get all Drawing node entries at a Location.
   * Optional `filter` predicate can be used to further filter the results.
   */
  getDrawingEntries(
    editor: Editor,
    options: {
      at?: Location;
      filter?: (node: DrawingElement) => boolean;
    }
  ): NodeEntry<DrawingElement>[] {
    const { at = editor.selection, filter = () => true } = options;
    if (!at) return [];

    return [
      ...Editor.nodes<DrawingElement>(editor, {
        at,
        match: n => isDrawingElement(n) && filter(n),
        voids: true,
      }),
    ];
  },

  getDrawingNonVisualProperties: (drawing: DrawingElement): cNvPr => {
    return drawing?.inline?.graphic?.graphicData?.pic?.nvPicPr?.cNvPr ?? {};
  },

  getDrawingVisualProperties: (drawing: DrawingElement): VisualProperties => {
    const transforms = drawing?.inline?.graphic?.graphicData?.pic?.spPr?.xfrm;
    return {
      flippedHorizontal: transforms?.flipH === '1',
      flippedVertical: transforms?.flipV === '1',
      rotation: stAngleToDegrees(parseInt(transforms?.rot ?? '0')),
    };
  },
};
